/**
 * PeriodDateSelector Component
 * Dropdown for selecting specific historical periods (Q1 2024, January 2024, etc.)
 */

import React, { useMemo, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { clsx } from 'clsx';
import type { PeriodType } from '../../types';

interface PeriodOption {
  label: string;
  startDate: string;
  endDate: string;
}

export interface SelectedPeriod {
  startDate: string;
  endDate: string;
}

export interface PeriodDateSelectorProps {
  periodType: PeriodType;
  selectedPeriod: SelectedPeriod;
  onChange: (period: SelectedPeriod) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Generate period options based on period type
 */
function generatePeriodOptions(periodType: PeriodType): PeriodOption[] {
  const options: PeriodOption[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  switch (periodType) {
    case 'monthly':
      // Generate last 12 months
      for (let i = 0; i < 12; i++) {
        const year = currentYear - Math.floor((currentMonth - i + 12) / 12) + 1;
        const month = (currentMonth - i + 12) % 12;
        const startDate = new Date(year, month, 1);
        const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

        // Only include completed or current periods
        if (startDate <= now) {
          options.push({
            label: startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          });
        }
      }
      break;

    case 'quarterly':
      // Generate last 8 quarters (2 years)
      const currentQuarter = Math.floor(currentMonth / 3);
      for (let i = 0; i < 8; i++) {
        const totalQuarters = currentQuarter + currentYear * 4 - i;
        const year = Math.floor(totalQuarters / 4);
        const quarter = totalQuarters % 4;
        const startMonth = quarter * 3;
        const startDate = new Date(year, startMonth, 1);
        const endDate = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);

        if (startDate <= now) {
          options.push({
            label: `Q${quarter + 1} ${year}`,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          });
        }
      }
      break;

    case 'half_yearly':
      // Generate last 4 half-years (2 years)
      const currentHalf = currentMonth < 6 ? 0 : 1;
      for (let i = 0; i < 4; i++) {
        const totalHalves = currentHalf + currentYear * 2 - i;
        const year = Math.floor(totalHalves / 2);
        const half = totalHalves % 2;
        const startMonth = half * 6;
        const startDate = new Date(year, startMonth, 1);
        const endDate = new Date(year, startMonth + 6, 0, 23, 59, 59, 999);

        if (startDate <= now) {
          options.push({
            label: `${half === 0 ? 'H1' : 'H2'} ${year}`,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          });
        }
      }
      break;

    case 'yearly':
      // Generate last 3 years
      for (let i = 0; i < 3; i++) {
        const year = currentYear - i;
        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31, 23, 59, 59, 999);

        options.push({
          label: `${year}`,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        });
      }
      break;
  }

  return options;
}

/**
 * Get default period for a given period type (current period)
 */
export function getDefaultPeriod(periodType: PeriodType): SelectedPeriod {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  let startDate: Date;
  let endDate: Date;

  switch (periodType) {
    case 'monthly':
      startDate = new Date(currentYear, currentMonth, 1);
      endDate = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59, 999);
      break;
    case 'quarterly':
      const quarter = Math.floor(currentMonth / 3);
      startDate = new Date(currentYear, quarter * 3, 1);
      endDate = new Date(currentYear, quarter * 3 + 3, 0, 23, 59, 59, 999);
      break;
    case 'half_yearly':
      const half = currentMonth < 6 ? 0 : 6;
      startDate = new Date(currentYear, half, 1);
      endDate = new Date(currentYear, half + 6, 0, 23, 59, 59, 999);
      break;
    case 'yearly':
      startDate = new Date(currentYear, 0, 1);
      endDate = new Date(currentYear, 11, 31, 23, 59, 59, 999);
      break;
    default:
      startDate = new Date(currentYear, 0, 1);
      endDate = now;
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

export const PeriodDateSelector: React.FC<PeriodDateSelectorProps> = ({
  periodType,
  selectedPeriod,
  onChange,
  disabled = false,
  className,
}) => {
  // Generate period options based on period type
  const periodOptions = useMemo(() => generatePeriodOptions(periodType), [periodType]);

  // When period type changes, reset to current period
  useEffect(() => {
    const defaultPeriod = getDefaultPeriod(periodType);
    // Check if current selection is valid for new period type
    const isValid = periodOptions.some(
      (opt) => opt.startDate === selectedPeriod.startDate && opt.endDate === selectedPeriod.endDate
    );
    if (!isValid) {
      onChange(defaultPeriod);
    }
  }, [periodType, periodOptions, selectedPeriod, onChange]);

  const selectedValue = `${selectedPeriod.startDate}|${selectedPeriod.endDate}`;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [startDate, endDate] = e.target.value.split('|');
    onChange({ startDate, endDate });
  };

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <Calendar className="w-4 h-4 text-stone-400" />
      <select
        value={selectedValue}
        onChange={handleChange}
        disabled={disabled}
        className={clsx(
          'px-3 py-2 rounded-lg border border-stone-200 bg-white text-stone-700',
          'text-sm font-medium',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
          'disabled:bg-stone-100 disabled:cursor-not-allowed'
        )}
      >
        {periodOptions.map((option) => (
          <option
            key={`${option.startDate}|${option.endDate}`}
            value={`${option.startDate}|${option.endDate}`}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PeriodDateSelector;
